import { useMemo } from "react"
import { OutOfPocketMaxState, QmeState, ReimbursementRule } from "#/lib/carrot-api/types/Subsidy"

export type Percentages = {
  companyResponsibility: number
  memberResponsibility: number
}

export type ReimbursementRulePercentages = {
  isSingleRule: boolean
  hasQmeRule: boolean
  hasOopMaxRule: boolean
  getPercentages: (qmeState: QmeState, oopState: OutOfPocketMaxState) => Percentages
}

/**
 * Extracts reimbursement percentages from reimbursement rules for cost share display.
 * Memoizes all percentages to avoid unnecessary recalculations on re-renders.
 */
export function useReimbursementRulePercentages(reimbursementRules: ReimbursementRule[]): ReimbursementRulePercentages {
  return useMemo(() => {
    const hasQmeRule = reimbursementRules.some((rule) => rule.qmeState !== QmeState.ANY)
    const hasOopMaxRule = reimbursementRules.some((rule) => rule.outOfPocketMaxState !== OutOfPocketMaxState.ANY)
    const isSingleRule = reimbursementRules.length === 1

    const resolvePercentage = (
      qmeState: QmeState,
      oopState: OutOfPocketMaxState
    ): ReimbursementRule["reimbursementPercent"] | null => {
      const exact = reimbursementRules.find(
        (rule) => rule.qmeState === qmeState && rule.outOfPocketMaxState === oopState
      )
      if (exact) return exact.reimbursementPercent

      const qmeAny = reimbursementRules.find(
        (rule) => rule.qmeState === qmeState && rule.outOfPocketMaxState === OutOfPocketMaxState.ANY
      )
      if (qmeAny) return qmeAny.reimbursementPercent

      const anyOop = reimbursementRules.find(
        (rule) => rule.qmeState === QmeState.ANY && rule.outOfPocketMaxState === oopState
      )
      if (anyOop) return anyOop.reimbursementPercent

      const anyAny = reimbursementRules.find(
        (rule) => rule.qmeState === QmeState.ANY && rule.outOfPocketMaxState === OutOfPocketMaxState.ANY
      )
      if (anyAny) return anyAny.reimbursementPercent

      return null
    }

    const toPercentages = (reimbursementPercent: number | null): Percentages | null => {
      if (reimbursementPercent === null) return null
      return {
        companyResponsibility: reimbursementPercent,
        memberResponsibility: 100 - reimbursementPercent
      }
    }

    const getPercentages = (qmeState: QmeState, oopState: OutOfPocketMaxState): Percentages => {
      const resolved = resolvePercentage(qmeState, oopState)
      const percentages = toPercentages(resolved)
      if (!percentages) {
        throw new Error(
          `Missing reimbursement rule for qmeState=${qmeState} oopState=${oopState} (including ANY fallbacks)`
        )
      }
      return percentages
    }

    return {
      isSingleRule,
      hasQmeRule,
      hasOopMaxRule,
      getPercentages
    }
  }, [reimbursementRules])
}
